/**
 * Renderer-side date-text normalization for the wizard's date-step (027 US6,
 * FR-026). The member types Finnish `pp.kk.vvvv`; the ENGINE's value model is
 * strict ISO `YYYY-MM-DD` (`submit-message.service.ts` validates the shape,
 * `ppj-payload-mapping.ts` refuses anything else), so the renderer owns the
 * translation in BOTH directions and never stores a raw Finnish string in the
 * values map. Pure module — trivially unit-testable.
 *
 * Accepted input: 1-2 digit day + 1-2 digit month + 4-digit year, dot-
 * separated (`1.2.2020`, `01.02.2020`), surrounding whitespace tolerated.
 * A 4-digit year is required — `pp.kk.vvvv` is the documented format cue
 * (FR-003), and two-digit years would force a century guess the engine
 * cannot verify. Impossible calendar components (month 13, 31.2, 29.2 off
 * leap years) are rejected rather than normalized into a different date.
 */

const FINNISH_DATE_PATTERN = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The explicit Gregorian leap rule — NOT `Date`, whose two-digit-year
 * coercion would silently turn year 0099 into 1999 (MED-1). */
function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** Days per month, `month` 1-12. */
function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return DAYS_PER_MONTH[month - 1] ?? 0;
}

/**
 * Parse typed `pp.kk.vvvv` text to the engine's ISO `YYYY-MM-DD`, or `null`
 * when the text is not a complete, real calendar date.
 */
export function parseFinnishDateToIso(input: string): string | null {
  const match = FINNISH_DATE_PATTERN.exec(input.trim());
  if (match === null || match[3] === undefined) {
    return null;
  }
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  // Years below 1000 are REJECTED as a typo class (lead decision at PR-2
  // review, inverting the earlier preserve-verbatim posture): in this
  // context a 4-digit vvvv under 1000 is virtually always a slip, and
  // refusing at the boundary (P7 refuse-don't-guess) kills the whole
  // wrong-century class — the member gets the inline invalid-date error
  // instead of a wrong-century calendar or a downstream mapping refusal.
  if (year < 1000 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  // The MATCHED 4-digit year flows through verbatim: `Number(...)` would drop
  // leading zeros and emit a non-ISO `99-02-01` shape the engine refuses.
  return `${match[3]}-${mm}-${dd}`;
}

/**
 * Format a stored ISO value back to the unpadded Finnish display text the
 * member typed (or picked); the empty string for empty/malformed input so a
 * revisited step renders an empty field rather than garbage.
 */
export function formatIsoToFinnish(iso: string): string {
  const match = ISO_DATE_PATTERN.exec(iso);
  if (match === null) {
    return '';
  }
  // Belt-and-braces: the year renders 4-digit-padded even though parse
  // rejects sub-1000 years — format must not silently shorten an ISO year.
  return `${Number(match[3])}.${Number(match[2])}.${String(Number(match[1])).padStart(4, '0')}`;
}

/** Digits and dots only, surrounding whitespace tolerated. */
const TYPING_PATTERN = /^\s*[\d.]*\s*$/;

/**
 * Insert the `pp.kk.vvvv` dots while the member types (`01022026` →
 * `01.02.2026`, `1.12` → `1.12.`). Text shaping only — never a parse. Only
 * the part after the last dot is split, so a day or month already closed by
 * a dot is never re-split into a different date (`123.2020` is left for the
 * parser to refuse). Text that is a prefix of `previous` (a deletion at the
 * end, or a paste of such a prefix) or has anything but digits and dots
 * comes back unchanged, so backspace can remove an inserted dot and the
 * invalid-date error still sees what was typed.
 */
export function formatDateTyping(previous: string, next: string): string {
  if (previous.startsWith(next) || !TYPING_PATTERN.test(next)) {
    return next;
  }
  // A dot typed straight after an inserted one must not double it.
  const parts = next
    .trim()
    .replace(/\.{2,}/g, '.')
    .split('.');
  const fields = parts.slice(0, -1);
  if (fields.length > 2 || fields.some((field) => field.length > 2)) {
    return next;
  }
  let rest = parts.at(-1) ?? '';
  while (fields.length < 2 && rest.length > 2) {
    fields.push(rest.slice(0, 2));
    rest = rest.slice(2);
  }
  fields.push(rest);
  const joined = fields.join('.');
  return fields.length < 3 && rest.length === 2 ? `${joined}.` : joined;
}
